import * as React from 'react'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { KIND, SHAPE } from 'baseui/button'
import ButtonCustom from '@/components/ButtonCustom'
import { SizeButton } from '@/constants/sizeButton'
import ModalCustomSize from '@/components/ShowModal/ModalCustomSize'
import Icons from '@/scenes/Editor/components/Icons'
import { ResizeSizeRow } from './ResizeSizeRow'
import type { Unit } from '@/utils/unit'

interface CustomSizeEditorOptions {
  initialWidth?: number
  initialHeight?: number
  initialUnit?: Unit
  initialAspectRatio?: number
  /** Controlled overrides — lets editor keep the row in sync with frame/undo */
  displayWidth?: number
  displayHeight?: number
  displayUnit?: Unit
  requirePremium?: boolean
  onPremiumRequired?: (proceed: () => void) => void
  onApply: (result: { width: number; height: number; unit: Unit }) => void
}

/**
 * The custom-size dialog and its state, for any trigger (a row, a tile).
 * Render `modal` once; call `open` from the trigger.
 */
export function useCustomSizeEditor({
  initialWidth = 0,
  initialHeight = 0,
  initialUnit = 'px',
  initialAspectRatio = 1,
  displayWidth,
  displayHeight,
  displayUnit,
  requirePremium = false,
  onPremiumRequired,
  onApply,
}: CustomSizeEditorOptions) {
  const [isOpen, setIsOpen] = useState(false)
  const [unit, setUnit] = useState<Unit>(initialUnit)
  const [aspectRatio, setAspectRatio] = useState(initialAspectRatio)
  const [customSize, setCustomSize] = useState({ width: initialWidth, height: initialHeight })

  useEffect(() => {
    if (isOpen) return
    if (displayWidth !== undefined) setCustomSize(s => ({ ...s, width: displayWidth }))
    if (displayHeight !== undefined) setCustomSize(s => ({ ...s, height: displayHeight }))
    if (displayUnit !== undefined) setUnit(displayUnit)
    if (displayWidth !== undefined && displayHeight !== undefined && displayHeight > 0) {
      setAspectRatio(displayWidth / displayHeight)
    }
  }, [displayWidth, displayHeight, displayUnit, isOpen])

  const open = () => {
    if (requirePremium && onPremiumRequired) {
      onPremiumRequired(() => setIsOpen(true))
      return
    }
    setIsOpen(true)
  }

  const modal = (
    <ModalCustomSize
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      customSize={customSize}
      setCustomSize={setCustomSize}
      applyCustomSize={({ width, height }) => {
        onApply({ width, height, unit })
      }}
      aspectRatio={aspectRatio}
      setAspectRatio={setAspectRatio}
      unit={unit}
      setUnit={(u: any) => setUnit(u)}
    />
  )

  return {
    open,
    modal,
    shownWidth: displayWidth ?? customSize.width,
    shownHeight: displayHeight ?? customSize.height,
    shownUnit: displayUnit ?? unit,
  }
}

interface CustomSizeButtonProps {
  active?: boolean
  initialWidth?: number
  initialHeight?: number
  initialUnit?: Unit
  initialAspectRatio?: number
  /** Controlled overrides — lets editor keep the row in sync with frame/undo */
  displayWidth?: number
  displayHeight?: number
  displayUnit?: Unit
  dimensionSeparator?: string
  requirePremium?: boolean
  onPremiumRequired?: (proceed: () => void) => void
  onClick?: () => void
  onApply: (result: { width: number; height: number; unit: Unit }) => void
  premiumBadge?: React.ReactNode
  expandedContent?: React.ReactNode
  className?: string
}

export function CustomSizeButton({
  active = false,
  initialWidth = 0,
  initialHeight = 0,
  initialUnit = 'px',
  initialAspectRatio = 1,
  displayWidth,
  displayHeight,
  displayUnit,
  dimensionSeparator = 'x',
  requirePremium = false,
  onPremiumRequired,
  onClick,
  onApply,
  premiumBadge,
  expandedContent,
  className,
}: CustomSizeButtonProps) {
  const { t } = useTranslation()
  const editor = useCustomSizeEditor({
    initialWidth,
    initialHeight,
    initialUnit,
    initialAspectRatio,
    displayWidth,
    displayHeight,
    displayUnit,
    requirePremium,
    onPremiumRequired,
    onApply,
  })
  const { shownWidth, shownHeight, shownUnit } = editor

  const handleRowClick = () => {
    onClick?.()
    editor.open()
  }

  const description =
    active && (shownWidth || shownHeight)
      ? `${shownWidth} ${dimensionSeparator} ${shownHeight} ${t(shownUnit)}`
      : undefined

  const adjustButton = active ? (
    <ButtonCustom
      shape={SHAPE.square}
      kind={KIND.minimal}
      type={SizeButton.SMALL}
      onClick={(e) => {
        e.stopPropagation()
        editor.open()
      }}
    >
      <Icons.Adjust size={24} />
    </ButtonCustom>
  ) : null

  return (
    <>
      <ResizeSizeRow
        name={t('Custom size')}
        description={description}
        icon={Icons.CustomSize}
        active={active}
        onClick={handleRowClick}
        premiumBadge={premiumBadge}
        rightAdornment={adjustButton}
        appendUnitLabel={false}
        className={className}
        expandedContent={expandedContent}
      />
      {editor.modal}
    </>
  )
}
